"use client"

import React, { useState, useEffect } from 'react'
import { Card, CardContent } from '../../../shared/components/ui/card'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList } from 'recharts'
import { useRegionId } from '../../../shared/hooks/use-region-id'
import { authFetchJson } from '@/shared/lib/auth-fetch'
import ReportPieChart from './report-pie-chart'

type WorkscopeData = {
  id: number;
  name: string;
  total: number;
  complete: number;
  incomplete: number;
};

interface WorkScopePriority {
  workScope: string;
  workScopeId: number;
  workScopeDisplay: string;
  priorities: {
    ready: Array<{ name: string; homeId: number; status: string }>;
    notReady: Array<{ name: string; homeId: number; status: string }>;
    goodwill?: Array<{ name: string; homeId: number; status: string }>;
  };
  completed: number;
  total: number;
  totalNeeded: number;
  percentage: number;
  goodwillCompleted?: number;
  goodwillTotalNeeded?: number;
}

type GoodwillWorkscopeCompletionPieChartProps = {
  priorityData: WorkScopePriority[]
}

// Helper function to format work scope names
const formatWorkScopeName = (name: string): string => {
  if (!name) return 'Unknown';
  
  // Convert to lowercase for consistent matching
  const lowerName = name.toLowerCase();
  
  // Special case mappings for both slug and display formats
  const specialCases: Record<string, string> = {
    'heavy-equipment-demo': 'Heavy Equipment',
    'heavy equipment/demo': 'Heavy Equipment',
    'heavy equipment': 'Heavy Equipment',
    'exterior-debris-removal': 'Ext Debris',
    'exterior debris': 'Ext Debris',
    'hvac-final': 'HVAC',
    'hvac': 'HVAC',
    'm-and-g-crawl-space': 'M&G Crawl',
    'm&g crawl space': 'M&G Crawl',
    'insulate-crawl-space': 'Insulate Crawl',
    'insulate crawl space': 'Insulate Crawl',
    'mud-and-tape': 'Mud & Tape',
    'muck and tape': 'Mud & Tape',
    'mud and tape': 'Mud & Tape',
    'Muck And Tape': 'Mud & Tape',
    'Mud And Tape': 'Mud & Tape',
    'muck-and-tape': 'Mud & Tape',
    'muck-and-gut-interior': 'Muck & Gut',
    'muck and gut': 'Muck & Gut'
  };
  
  // Check for special case first (try exact match, then lowercase match)
  if (specialCases[name]) {
    return specialCases[name];
  }
  if (specialCases[lowerName]) {
    return specialCases[lowerName];
  }
  
  // Handle already formatted strings (with spaces)
  let result = '';
  if (name.includes(' ')) {
    // Capitalize each word, handling & specially
    result = name
      .split(/[\s/]+/)
      .map(word => {
        if (!word) return '';
        // If word contains &, capitalize letters on both sides
        if (word.includes('&')) {
          const parts = word.split('&');
          return parts
            .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
            .join('&');
        }
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .filter(word => word.length > 0)
      .join(' ');
  } else {
    // Handle slug format (with hyphens/underscores)
    result = name
      .split(/[-_]/)
      .map(word => {
        if (!word) return '';
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .filter(word => word.length > 0)
      .join(' ');
  }
  
  // Remove redundant "Install" and "Work" from work scope names for brevity
  result = result
    .replace(/^Install\s+/i, '') // Remove "Install" from the beginning
    .replace(/\s+Work$/i, '') // Remove "Work" from the end
    .trim(); // Remove any leading/trailing whitespace
  
  // Convert HVAC to all uppercase
  result = result.replace(/\bHvac\b/gi, 'HVAC');
  
  return result;
};

function WorkscopeStatusBarChart() {
  const { regionId, isLoading: isLoadingRegion } = useRegionId()
  const [data, setData] = useState<WorkscopeData[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isMobile, setIsMobile] = useState(false)

  // Detect mobile screen size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }
    
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    const fetchData = async () => {
      if (!regionId) {
        // Wait for regionId to be available
        setIsLoading(true)
        return
      }

      try {
        setIsLoading(true)
        setError(null)
        
        // Fetch priority data which now includes work scope metadata
        const res = await authFetchJson<any>(`/priority?regionId=${encodeURIComponent(regionId)}`)
        if (!res.success) {
          throw new Error(res.message || 'Failed to fetch data')
        }
        const result = res.data
        
        // Handle different response formats
        let priorityData: WorkScopePriority[]
        if (Array.isArray(result)) {
          priorityData = result
        } else if (result.success && Array.isArray(result.data)) {
          priorityData = result.data
        } else if (result.data && Array.isArray(result.data)) {
          priorityData = result.data
        } else {
          throw new Error('Invalid response format from priority API')
        }

        // Map the data to chart format
        const chartData = priorityData
          .sort((a, b) => a.workScopeId - b.workScopeId)
          .map(workScopeData => {
            const total = workScopeData.totalNeeded || 0
            const complete = workScopeData.completed || 0
            const incomplete = total - complete
            // Use workScopeDisplay if available, otherwise format workScope
            const rawName = workScopeData.workScopeDisplay || workScopeData.workScope || 'Unknown'
            const displayName = formatWorkScopeName(rawName)

            return {
              id: workScopeData.workScopeId,
              name: displayName,
              total,
              complete,
              incomplete
            }
          })
          .filter(item => item.total > 0) // Only show work scopes with homes

        setData(chartData)
      } catch (err) {
        console.error('Error fetching workscope data:', err)
        setError('Failed to load workscope data')
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [regionId])

  if (isLoadingRegion || isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <span className="ml-2">Loading workscope data...</span>
      </div>
    )
  }

  if (!regionId) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">No region selected</p>
      </div>
    )
  }

  if (error || data.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">{error || 'No workscope data available'}</p>
      </div>
    )
  }

  if (isMobile) {
    return (
      <Card className="shadow-md rounded-2xl">
        <CardContent className="space-y-2 p-3">
          {data.map((item) => {
            const percent = item.total > 0 ? Math.round((item.complete / item.total) * 100) : 0
            const toGo = Math.max(0, item.total - item.complete)

            return (
              <div key={item.id} className="space-y-1">
                <div className="flex items-baseline justify-between gap-2 text-[11px]">
                  <span className="truncate font-semibold text-gray-700">{item.name}</span>
                  <span className="shrink-0 text-muted-foreground">
                    {percent}% • {toGo} to go
                  </span>
                </div>
                <div className="flex h-3 overflow-hidden rounded-full bg-[#C9A961]">
                  <div
                    className="bg-[#3E7C59]"
                    style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
                  />
                </div>
              </div>
            )
          })}
        </CardContent>
      </Card>
    )
  }

  // Responsive chart settings
  const chartSettings = isMobile 
    ? {
        margin: { top: 60, right: 10, left: 5, bottom: 5 },
        yAxisWidth: 90,
        fontSize: 7,
        barGap: "8%",
        labelFontSize: 9
      }
    : {
        margin: { top: 5, right: 10, left: 180, bottom: 5 },
        yAxisWidth: 180,
        fontSize: 13,
        barGap: "15%",
        labelFontSize: 12
      }

  return (
    <div className="grid gap-4">
      <style>{`
        .workscope-chart svg text {
          white-space: nowrap !important;
        }
        .workscope-chart {
          width: 100% !important;
        }
        .workscope-chart .recharts-wrapper {
          width: 100% !important;
        }
        .workscope-chart .recharts-legend-wrapper {
          width: 100% !important;
          left: 0 !important;
          right: 0 !important;
        }
      `}</style>
      <Card className="shadow-md rounded-2xl">
        <CardContent className={`h-[500px] md:h-[800px] ${isMobile ? 'p-0 px-1' : 'p-2'} overflow-x-auto workscope-chart`}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={chartSettings.margin} barCategoryGap={chartSettings.barGap}>
              <XAxis type="number" hide />
              <YAxis 
                dataKey="name" 
                type="category" 
                width={chartSettings.yAxisWidth}
                interval={0}
                tick={{ fontSize: chartSettings.fontSize, fill: '#374151', fontWeight: 600, textAnchor: 'end', style: { whiteSpace: 'nowrap' } }} 
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                cursor={{ fill: 'rgba(128,128,128,0.1)' }}
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const item = payload[0].payload;
                    const percent = Math.round((item.complete / item.total) * 100);
                    return (
                      <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
                        <p className="font-semibold text-gray-800 mb-2">{item.name}</p>
                        <p className="text-sm text-gray-600">To Go: {item.incomplete}</p>
                        <p className="text-sm text-gray-600">Complete: {item.complete}</p>
                        <p className="text-sm text-gray-600">Total: {item.total}</p>
                        <p className="text-sm font-semibold text-gray-800 mt-1">{percent}% Complete</p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Bar dataKey="complete" stackId="a" fill="#3E7C59" name="Complete" radius={[0, 0, 0, 0]}>
                <LabelList
                  dataKey={(entry: any) => {
                    const percent = Math.round((entry.complete / entry.total) * 100);
                    return percent >= 15 ? `${percent}%` : '';
                  }}
                  position="center"
                  fill="#fff"
                  fontSize={chartSettings.labelFontSize}
                  style={{ whiteSpace: 'pre', textAlign: 'center', fontWeight: '500' } as React.CSSProperties}
                />
              </Bar>
              <Bar dataKey="incomplete" stackId="a" fill="#C9A961" name="To Go" radius={[0, 4, 4, 0]}>
                <LabelList
                  dataKey={(entry: any) => {
                    const toGo = entry.total - entry.complete;
                    return toGo > 0 ? `${toGo}` : '';
                  }}
                  position="center"
                  fill="#fff"
                  fontSize={chartSettings.labelFontSize}
                  style={{ textAlign: 'center', fontWeight: '500' } as React.CSSProperties}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* <Card className="shadow-md rounded-2xl">
        <CardContent className="pt-6">
          <ul className="divide-y divide-gray-200">
            {data.map((w) => {
              const percent = Math.round((w.complete / w.total) * 100);
              const toGo = w.total - w.complete;
              return (
                <li key={w.id} className="flex justify-between items-center py-2 hover:bg-gray-100">
                  <span className="font-medium text-gray-800 w-32 text-sm">{w.name}</span>
                  <div className="flex items-center gap-4 w-1/2">
                    <div className="flex-grow bg-gray-200 rounded-full h-3 relative overflow-hidden">
                      <div
                        className="bg-[#3E7C59] h-3 rounded-l-full text-[7px] text-white flex items-center justify-center"
                        style={{ width: `${percent}%` }}
                      >
                        {percent > 8 && `${w.complete} / ${w.total} (${percent}%)`}
                      </div>
                      <div
                        className="absolute top-0 h-3 flex items-center justify-center text-[7px] text-black"
                        style={{ left: `${percent}%`, width: `${100 - percent}%` }}
                      >
                        {toGo > 0 && `${toGo}`}
                      </div>
                    </div>
                    <div className="text-xs text-gray-600 w-44 text-right">
                      {w.complete} complete / {toGo} to go <br />
                      <span className="text-gray-500">({w.total} total)</span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card> */}
    </div>
  );
}

export function GoodwillWorkscopeCompletionPieChart({
  priorityData,
}: GoodwillWorkscopeCompletionPieChartProps) {
  const hasGoodwillWorkscopeTotals = priorityData.some(
    (workScopeData) =>
      workScopeData.goodwillCompleted !== undefined ||
      workScopeData.goodwillTotalNeeded !== undefined
  )
  const goodwillComplete = priorityData.reduce((count, workScopeData) => (
    count + (workScopeData.goodwillCompleted || 0)
  ), 0)
  const goodwillTotal = priorityData.reduce((count, workScopeData) => (
    count + (workScopeData.goodwillTotalNeeded || 0)
  ), 0)
  const goodwillToGo = Math.max(0, goodwillTotal - goodwillComplete)
  const goodwillPercentage = goodwillTotal > 0
    ? Math.round((goodwillComplete / goodwillTotal) * 100)
    : 0

  if (priorityData.length > 0 && !hasGoodwillWorkscopeTotals) {
    return (
      <div className="flex h-[280px] flex-col items-center justify-center text-center">
        <p className="font-semibold">Goodwill Workscopes Status</p>
        <p className="mt-2 max-w-xs text-sm text-muted-foreground">
          Refresh the Priority Matrix to calculate Goodwill workscope completion.
        </p>
      </div>
    )
  }

  return (
    <ReportPieChart
      title="Goodwill Workscopes Status"
      numerator={goodwillComplete}
      denominator={goodwillTotal}
      percentage={goodwillPercentage}
      chartData={[
        { key: 'completed', value: goodwillComplete, fill: "#3E7C59" },
        { key: 'todo', value: goodwillToGo, fill: "#C9A961" },
      ]}
      chartConfig={{
        completed: {
          label: "Complete",
          color: "green",
        },
        todo: {
          label: "To Go",
          color: "yellow",
        },
      }}
    />
  )
}

export default WorkscopeStatusBarChart;

